import { Route } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { LandingComponent } from './pages/landing/landing.component';
import { AuthGuard } from './utilities/auth.gaurd';
import { routeUrls } from './constants/constants';
import { RoleGuard, TreasurerGuard } from './utilities/routeAccess.guard';
import { AdminLayoutComponent } from './pages/admin/admin-layout/admin-layout.component';
import { AdminGuard } from './utilities/admin.guard';
import { FeatureFlagGuard } from './utilities/featureFlag.guard';
import { SubscriptionPlanGuard } from './utilities/subscriptionPlan.guard';
import { MaintenanceGuard } from './utilities/maintenance.guard';
import { SignedInRedirectGuard } from './utilities/signed-in-redirect.guard';

const constants = routeUrls;

export const appRoutes: Route[] = [
  {
    path: '',
    component: LandingComponent,
    pathMatch: 'full',
  },
  {
    path: constants.HOME,
    component: LandingComponent,
  },
  {
    path: 'register-club',
    component: LandingComponent,
    canActivate: [SignedInRedirectGuard],
    data: { openModal: true },
  },
  {
    path: 'contact-us',
    component: LandingComponent,
    data: { section: 'contact' },
  },
  {
    path: 'key-features',
    component: LandingComponent,
    data: { section: 'key-features' },
  },
  {
    path: constants.HANDLE_INVITE,
    loadComponent: () =>
      import('./pages/onboarding/handle-invite/handle-invite.component').then(
        (m) => m.HandleInviteComponent
      ),
  },
  {
    path: constants.AUTH_CALLBACK,
    loadComponent: () =>
      import('./auth/auth-callback/auth-callback.component').then(
        (m) => m.AuthCallbackComponent
      ),
  },
  {
    path: constants.PREDASHBOARD,
    loadComponent: () =>
      import('./pages/onboarding/pre-dashboard/pre-dashboard.component').then(
        (m) => m.PreDashboardComponent
      ),
  },
  {
    path: constants.TERMS,
    loadComponent: () =>
      import('./pages/miscellaneous/terms-of-use/terms-of-use.component').then(
        (m) => m.TermsOfUseComponent
      ),
  },
  {
    path: constants.PRIVACY_POLICY,
    loadComponent: () =>
      import(
        './pages/miscellaneous/privacy-policy/privacy-policy.component'
      ).then((m) => m.PrivacyPolicyComponent),
  },
  {
    path: constants.COOKIE_POLICY,
    loadComponent: () =>
      import(
        './pages/miscellaneous/cookie-policy/cookie-policy.component'
      ).then((m) => m.CookiePolicyComponent),
  },
  {
    path: constants.PRICING,
    loadComponent: () =>
      import('./pages/pricing/pricing.component').then(
        (m) => m.PricingComponent
      ),
  },
  {
    path: 'about',
    loadComponent: () =>
      import('./pages/miscellaneous/about/about.component').then(
        (m) => m.AboutComponent
      ),
  },
  {
    path: constants.CONFIRMATION,
    loadComponent: () =>
      import(
        './pages/onboarding/mail-confirmation/mail-confirmation.component'
      ).then((m) => m.MailConfirmationComponent),
  },
  {
    path: constants.INVALID_LINK,
    loadComponent: () =>
      import('./pages/onboarding/invalid-llink/invalid-link.component').then(
        (m) => m.InvalidLinkComponent
      ),
  },
  {
    path: constants.ADMIN,
    canActivate: [AdminGuard],
    component: AdminLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: constants.ADMIN_DASHBOARD,
        pathMatch: 'full',
      },
      {
        path: constants.ADMIN_DASHBOARD,
        loadComponent: () =>
          import(
            './pages/admin/admin-dashboard/admin-dashboard.component'
          ).then((m) => m.AdminDashboardComponent),
      },
      {
        path: constants.ADMIN_CLUBS,
        loadComponent: () =>
          import('./pages/admin/club-list/club-list.component').then(
            (m) => m.ClubListComponent
          ),
      },
      {
        path: constants.ADMIN_LEAGUES,
        loadComponent: () =>
          import('./pages/admin/leagus/add-leagues/add-leagues.component').then(
            (m) => m.AddLeaguesComponent
          ),
      },
      {
        path: constants.ADMIN_LIST_LEAGUES,
        loadComponent: () =>
          import('./pages/admin/leagus/league-list/league-list.component').then(
            (m) => m.LeagueListComponent
          ),
      },
      {
        path: constants.ADMIN_GROUNDS,
        loadComponent: () =>
          import('./pages/admin/ground/add-ground/add-ground.component').then(
            (m) => m.AddGroundComponent
          ),
      },
      {
        path: constants.ADMIN_LIST_GROUNDS,
        loadComponent: () =>
          import('./pages/admin/ground/ground-list/ground-list.component').then(
            (m) => m.GroundListComponent
          ),
      },
      {
        path: constants.ADMIN_FEATURE_FLAGS,
        loadComponent: () =>
          import('./pages/admin/feature-flags/admin-feature-flags.component').then(
            (m) => m.AdminFeatureFlagsComponent
          ),
      },
      {
        path: constants.ADMIN_MAINTENANCE,
        loadComponent: () =>
          import('./pages/admin/maintenance/admin-maintenance.component').then(
            (m) => m.AdminMaintenanceComponent
          ),
      }
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard, MaintenanceGuard],
    children: [
      {
        path: '',
        redirectTo: constants.DASHBOARD,
        pathMatch: 'full',
      },
      {
        path: constants.DASHBOARD,
        loadComponent: () =>
          import('./pages/dashboard/index/index.component').then(
            (m) => m.IndexComponent
          ),
      },
      {
        path: constants.TOURNAMENTS,
        loadComponent: () =>
          import('./pages/tournaments/tournaments.component').then(
            (m) => m.TournamentsComponent
          ),
      },
      {
        path: constants.ADD_TOURNAMENTS,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import(
            './pages/tournaments/add-tournament/add-tournament.component'
          ).then((m) => m.AddTournamentComponent),
      },
      {
        path: constants.LIST_TOURNAMENTS,
        loadComponent: () =>
          import(
            './pages/tournaments/tournaments-list/tournaments-list.component'
          ).then((m) => m.TournamentsListComponent),
      },
      {
        path: constants.ADD_LEAGUES,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/admin/leagus/add-leagues/add-leagues.component').then(
            (m) => m.AddLeaguesComponent
          ),
      },
      {
        path: constants.LEAGUES,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/leagues/league-index.component').then(
            (m) => m.LeagueIndexComponent
          ),
      },
      {
        path: constants.ADD_GROUNDS,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/admin/ground/add-ground/add-ground.component').then(
            (m) => m.AddGroundComponent
          ),
      },
      {
        path: constants.GROUNDS,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/grounds/ground-index.component').then(
            (m) => m.GroundIndexComponent
          ),
      },
      {
        path: constants.PRACTICES,
        loadComponent: () =>
          import('./pages/practices/practices-index.component').then(
            (m) => m.PracticesIndexComponent
          ),
      },
      {
        path: constants.ADD_PRACTICES,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/practices/add-practice/add-practice.component').then(
            (m) => m.AddPracticeComponent
          ),
      },
      {
        path: constants.LIST_PRACTICES,
        loadComponent: () =>
          import(
            './pages/practices/practice-list/practice-list.component'
          ).then((m) => m.PracticeListComponent),
      },
      {
        path: constants.MATCHES,
        loadComponent: () =>
          import('./pages/matches/match-index.component').then(
            (m) => m.MatchIndexComponent
          ),
      },
      {
        path: constants.ADD_MATCHES,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/matches/add-match/add-match.component').then(
            (m) => m.AddMatchComponent
          ),
      },
      {
        path: constants.LIST_MATCHES,
        loadComponent: () =>
          import('./pages/matches/match-list/match-list.component').then(
            (m) => m.MatchListComponent
          ),
      },
      {
        path: constants.PLAYERS,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/players/player-index.component').then(
            (m) => m.PlayerIndexComponent
          ),
      },
      {
        path: constants.ADD_PLAYERS,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/players/add-player/add-player.component').then(
            (m) => m.AddPLayerComponent
          ),
      },
      {
        path: constants.LIST_PlAYERS,
        canActivate: [RoleGuard],
        loadComponent: () =>
          import('./pages/players/player-list/player-list.component').then(
            (m) => m.PlayerListComponent
          ),
      },
      {
        path: `${constants.NOTICEBOARD}/${constants.NB_INDEX}`,
        loadComponent: () =>
          import('./pages/noticeboard/nb-index/nb-index.component').then(
            (m) => m.NbIndexComponent
          ),
      },
      {
        path: `${constants.NOTICEBOARD}/${constants.GALLERY}`,
        loadComponent: () =>
          import('./pages/noticeboard/gallery/gallery.component').then(
            (m) => m.GalleryComponent
          ),
      },
      {
        path: constants.EXPENSE_CREATE,
        canActivate: [FeatureFlagGuard],
        data: { featureFlag: 'expense-tracking' },
        loadComponent: () =>
          import('./pages/expenses/expense-create/expense-create.component').then(
            (m) => m.ExpenseCreateComponent
          ),
      },
      {
        path: constants.EXPENSE_EDIT,
        canActivate: [FeatureFlagGuard],
        data: { featureFlag: 'expense-tracking' },
        loadComponent: () =>
          import('./pages/expenses/expense-create/expense-create.component').then(
            (m) => m.ExpenseCreateComponent
          ),
      },
      {
        path: constants.EXPENSE_DETAIL,
        canActivate: [FeatureFlagGuard],
        data: { featureFlag: 'expense-tracking' },
        loadComponent: () =>
          import('./pages/expenses/expense-detail/expense-detail.component').then(
            (m) => m.ExpenseDetailComponent
          ),
      },
      {
        path: constants.EXPENSES,
        canActivate: [FeatureFlagGuard],
        data: { featureFlag: 'expense-tracking' },
        loadComponent: () =>
          import('./pages/expenses/expense-list/expense-list.component').then(
            (m) => m.ExpenseListComponent
          ),
      },
      {
        path: constants.FEE_PAYMENTS,
        loadComponent: () =>
          import('./pages/fee-payments/fee-payments.component').then(
            (m) => m.FeePaymentsComponent
          ),
      },
      {
        path: constants.SETTINGS,
        canActivate: [RoleGuard],
        children: [
          { path: '', redirectTo: 'general', pathMatch: 'full' as const },
          {
            path: 'general',
            loadComponent: () =>
              import('./pages/settings/general-settings/general-settings.component').then(
                (m) => m.GeneralSettingComponent
              ),
          },
          {
            path: 'fees',
            canActivate: [TreasurerGuard],
            loadComponent: () =>
              import('./pages/settings/fees/fees-list.component').then(
                (m) => m.FeesComponent
              ),
          },
          {
            path: 'expense-config',
            canActivate: [TreasurerGuard, FeatureFlagGuard, SubscriptionPlanGuard],
            data: { featureFlag: 'expense-tracking', minPlanLevel: 2 },
            loadComponent: () =>
              import('./pages/settings/expense-config/expense-config.component').then(
                (m) => m.ExpenseConfigComponent
              ),
          },
          {
            path: 'permission',
            loadComponent: () =>
              import('./pages/settings/permission/permission.component').then(
                (m) => m.PermissionComponent
              ),
          },
          {
            path: 'features',
            loadComponent: () =>
              import('./pages/settings/features/features-settings.component').then(
                (m) => m.FeaturesSettingsComponent
              ),
          },
          {
            path: 'subscription',
            loadComponent: () =>
              import('./pages/settings/setting-subscription/setting-subscription.component').then(
                (m) => m.CrickteamsSubscriptionComponent
              ),
          },
        ],
      },
      {
        path: `${constants.USER}/${constants.PROFILE}`,
        loadComponent: () =>
          import('./pages/user/user-profile.component').then(
            (m) => m.UserProfileComponent
          ),
      },
      {
        path: 'help-center',
        data: { embedded: true },
        loadComponent: () =>
          import('./pages/help-center/help-center.component').then(
            (m) => m.HelpCenterComponent
          ),
      },
      {
        path: constants.CHAT,
        canActivate: [FeatureFlagGuard],
        data: { featureFlag: 'club-chat' },
        loadComponent: () =>
          import('./pages/chat/chat.component').then(
            (m) => m.ChatComponent
          ),
      },
      {
        path: '**',
        redirectTo: constants.DASHBOARD,
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: constants.HOME,
    pathMatch: 'full',
  },
];
